import { Component } from '@theme/component';
import { observeCarouselImages } from '@theme/evenmorn-responsive-images';
import '@theme/evenmorn-marquee';

/** Native scroll-snap carousel; motion is manual and controls follow the actual viewport.
 * @extends {Component<{track: HTMLDivElement, controls: HTMLDivElement, dots: HTMLDivElement, previous: HTMLButtonElement, next: HTMLButtonElement}>}
 */
class EvenmornCarousel extends Component {
  #events;
  #visibility;
  #resize;
  #positions = [];
  #page = 0;
  #scrollFrame;
  #width = 0;
  #measureFrame;
  #controlsFrame;
  #syncFrame;
  requiredRefs = ['track', 'controls', 'dots', 'previous', 'next'];

  connectedCallback() {
    super.connectedCallback();
    this.#deferInitialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#deferInitialize();
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#visibility?.disconnect();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#scrollFrame);
    cancelAnimationFrame(this.#measureFrame);
    cancelAnimationFrame(this.#controlsFrame);
    cancelAnimationFrame(this.#syncFrame);
    super.disconnectedCallback();
  }

  #deferInitialize() {
    this.#events?.abort();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#scrollFrame);
    cancelAnimationFrame(this.#measureFrame);
    cancelAnimationFrame(this.#controlsFrame);
    cancelAnimationFrame(this.#syncFrame);
    this.#visibility?.disconnect();
    if (!('IntersectionObserver' in window) || window.Shopify?.designMode) {
      this.#initialize();
      return;
    }
    // Lower-page carousels keep native scrolling until they approach the viewport.
    this.#visibility = new IntersectionObserver((entries) => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      this.#visibility.disconnect();
      this.#initialize();
    }, { rootMargin: '400px' });
    this.#visibility.observe(this);
  }

  #initialize() {
    this.#events?.abort();
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#scrollFrame);
    cancelAnimationFrame(this.#measureFrame);
    cancelAnimationFrame(this.#controlsFrame);
    cancelAnimationFrame(this.#syncFrame);
    this.#events = new AbortController();
    const { signal } = this.#events;
    observeCarouselImages(this.refs.track, signal);
    this.refs.track.addEventListener('scroll', this.#sync, { passive: true, signal });
    this.refs.track.addEventListener('keydown', this.#keyboard, { signal });
    this.refs.track.addEventListener('pointerdown', this.#cancelScroll, { passive: true, signal });
    this.refs.track.addEventListener('wheel', this.#cancelScroll, { passive: true, signal });
    this.refs.dots.addEventListener('click', this.#choosePage, { signal });
    this.#resize = new ResizeObserver(this.#queueMeasure);
    this.#resize.observe(this.refs.track);
    this.#queueMeasure();
  }

  // ResizeObserver and section updates share one measurement per frame.
  #queueMeasure = () => {
    cancelAnimationFrame(this.#measureFrame);
    this.#measureFrame = requestAnimationFrame(this.#measure);
  };

  #measure = () => {
    const { track, dots, controls } = this.refs;
    if (track.clientWidth !== this.#width) this.#cancelScroll();
    this.#width = track.clientWidth;
    const item = track.firstElementChild;
    if (!item) { controls.hidden = true; return; }
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = item.getBoundingClientRect().width + gap;
    const visible = Math.max(1, Math.floor((track.clientWidth + gap + 1) / step));
    const last = Math.max(0, track.scrollWidth - track.clientWidth);
    this.#positions = [0];
    for (let x = step * visible; x < last - 1; x += step * visible) this.#positions.push(x);
    if (last > 1) this.#positions.push(last);
    // Read every carousel before any control insertion invalidates page layout.
    cancelAnimationFrame(this.#controlsFrame);
    this.#controlsFrame = requestAnimationFrame(() => {
      controls.hidden = last < 2 && this.dataset.staticControls !== 'true';
      if (dots.children.length !== this.#positions.length) {
        const focused = document.activeElement;
        const focusedPage = focused instanceof HTMLButtonElement && dots.contains(focused) ? Number(focused.dataset.page) : undefined;
        dots.replaceChildren(...this.#positions.map((_, i) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.page = String(i);
          return button;
        }));
        if (focusedPage !== undefined) {
          const button = dots.children[Math.min(focusedPage, dots.children.length - 1)];
          if (button instanceof HTMLButtonElement) button.focus({ preventScroll: true });
        }
      }
      for (const [i, button] of [...dots.children].entries()) {
        button.setAttribute('aria-controls', track.id);
        button.setAttribute('aria-label', (this.dataset.pageLabel || '{number}').replace('{number}', String(i + 1)));
      }
      this.#sync();
    });
  };

  #sync = () => {
    cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = requestAnimationFrame(this.#updateControls);
  };

  #updateControls = () => {
    const scroll = Math.abs(this.refs.track.scrollLeft);
    this.#page = this.#positions.reduce((best, x, i) => Math.abs(x - scroll) < Math.abs(this.#positions[best] - scroll) ? i : best, 0);
    this.refs.previous.disabled = this.#page === 0;
    this.refs.next.disabled = this.#page === this.#positions.length - 1;
    for (const [i, button] of [...this.refs.dots.children].entries()) {
      if (i === this.#page) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    }
  };

  /** @param {MouseEvent} event - A page dot, kept focused during scrolling. */
  #choosePage = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-page]');
    if (button instanceof HTMLButtonElement) this.#go(Number(button.dataset.page));
  };

  /** @param {KeyboardEvent} event - Arrow keys on the scroll region itself. */
  #keyboard = (event) => {
    if (event.target !== this.refs.track) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'Home') this.#go(0);
    else if (event.key === 'End') this.#go(this.#positions.length - 1);
    else if (event.key === 'ArrowRight') this.#go(this.#page + (rtl ? -1 : 1));
    else if (event.key === 'ArrowLeft') this.#go(this.#page + (rtl ? 1 : -1));
    else return;
    event.preventDefault();
  };

  /** Move one visible page backwards. */
  previous() { this.#go(this.#page - 1); }
  /** Move one visible page forwards. */
  next() { this.#go(this.#page + 1); }
  #cancelScroll = () => { cancelAnimationFrame(this.#scrollFrame); };

  #go(page) {
    this.#cancelScroll();
    const index = Math.max(0, Math.min(page, this.#positions.length - 1));
    const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    const target = this.#positions[index] * direction;
    const start = this.refs.track.scrollLeft;
    if (start === target || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.refs.track.scrollTo({ left: target, behavior: 'instant' });
      return;
    }
    const duration = this.classList.contains('ev-carousel--reviews') ? 420 : 300;
    const began = performance.now();
    const move = (now) => {
      const progress = Math.min(1, (now - began) / duration);
      this.refs.track.scrollTo({ left: start + (target - start) * (1 - (1 - progress) ** 3), behavior: 'instant' });
      if (progress < 1) this.#scrollFrame = requestAnimationFrame(move);
    };
    this.#scrollFrame = requestAnimationFrame(move);
  }
}

/** Purchase enhancement preserves native variant URLs and the Shopify product form.
 * @extends {Component<{form: HTMLFormElement, add: HTMLButtonElement, sticky: HTMLDivElement, timeline: HTMLOListElement}>}
 */
class EvenmornProduct extends Component {
  #events;
  #observer;
  #reveals;
  #frame;
  #animations = new Set();
  /** @type {{id: number, inventory_management?: string, inventory_quantity?: number}[]} */
  #variants = [];
  requiredRefs = ['form', 'add', 'sticky'];

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#observer?.disconnect();
    this.#reveals?.disconnect();
    cancelAnimationFrame(this.#frame);
    for (const animation of this.#animations) animation.cancel();
    this.#animations.clear();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#events?.abort();
    this.#observer?.disconnect();
    this.#reveals?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#events = new AbortController();
    try {
      this.#variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    const selectedVariant = this.refs.form.elements.namedItem('id');
    if (selectedVariant instanceof HTMLInputElement) this.#updateStock(selectedVariant.value);
    this.#syncPurchase();
    this.refs.form.addEventListener('input', this.#hideSticky, { signal: this.#events.signal });
    this.#observer = new IntersectionObserver(this.#updateSticky);
    this.#observer.observe(this.refs.add);
    // Capture also covers scroll containers used by preview/editor shells.
    document.addEventListener('scroll', this.#queueSticky, { capture: true, passive: true, signal: this.#events.signal });
    window.addEventListener('resize', this.#queueSticky, { passive: true, signal: this.#events.signal });
    this.#queueSticky();
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const pending = new WeakSet();
      this.#reveals = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!pending.has(entry.target)) {
            if (entry.boundingClientRect.top < innerHeight) {
              this.#reveals.unobserve(entry.target);
              continue;
            }
            pending.add(entry.target);
          }
          if (entry.isIntersecting && entry.intersectionRatio >= (entry.target.dataset.reveal === 'trust' ? .15 : .1)) {
            if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
              const duration = entry.target.dataset.reveal === 'trust' ? 800 : 600;
              const delay = Number(entry.target.dataset.revealDelay) || 0;
              const animation = entry.target.animate([{ opacity: .25, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration, delay, fill: 'backwards', easing: 'linear' });
              this.#animations.add(animation);
              animation.addEventListener('finish', () => this.#animations.delete(animation), { once: true });
            }
            this.#reveals.unobserve(entry.target);
          }
        }
      }, { threshold: [.1, .15] });
      for (const item of this.querySelectorAll('[data-reveal]')) this.#reveals.observe(item);
    }
  }

  #hideSticky = () => { this.refs.sticky.hidden = true; };
  #updateSticky = () => {
    const hidden = this.refs.add.getBoundingClientRect().bottom >= 0;
    const timeline = this.refs.timeline;
    let progress = 0;
    let active = [];
    if (timeline) {
      const rect = timeline.getBoundingClientRect();
      progress = rect.height ? Math.max(0, Math.min(1, (innerHeight * .7 - rect.top) / rect.height)) : 0;
      // Offscreen timelines need no child measurements. Read before changing sticky styles.
      active = [...timeline.children].map(item => progress >= 1 || (progress > 0 && item.getBoundingClientRect().top - rect.top + 20 <= progress * rect.height));
    }
    this.refs.sticky.hidden = hidden;
    if (!timeline) return;
    timeline.setAttribute('data-progress', '');
    timeline.style.setProperty('--ev-timeline-progress', String(progress));
    for (const [index, item] of [...timeline.children].entries()) item.toggleAttribute('data-active', active[index]);
  };
  #queueSticky = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#updateSticky);
  };

  /** @param {string} id - Selected pack ID from the native Shopify form. */
  #updateStock(id) {
    const variant = this.#variants.find(item => String(item.id) === id);
    const threshold = Number(this.dataset.stockThreshold);
    const qty = variant?.inventory_quantity;
    let stock = this.querySelector('[data-low-stock]');
    if (variant?.inventory_management !== 'shopify' || !Number.isInteger(qty) || !Number.isFinite(threshold) || threshold < 1 || qty < 1 || qty > threshold) {
      stock?.remove();
      return '';
    }
    if (!stock) {
      const template = this.querySelector('[data-stock-template]');
      if (!(template instanceof HTMLTemplateElement)) return '';
      const fragment = template.content.cloneNode(true);
      if (!(fragment instanceof DocumentFragment)) return '';
      stock = fragment.querySelector('[data-low-stock]');
      template.after(fragment);
    }
    const text = stock?.querySelector('[data-stock-text]');
    const bar = stock?.querySelector('[data-stock-bar]');
    const label = (this.dataset.stockLabel || '').replace('{qty}', String(qty));
    if (text) text.textContent = label;
    if (bar instanceof HTMLProgressElement) {
      bar.max = threshold;
      bar.value = qty;
      bar.setAttribute('aria-label', label);
    }
    return label;
  }

  /** @param {MouseEvent} event - Native pack link, enhanced only for an unmodified click. */
  selectPack(event) {
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[data-variant]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const input = this.refs.form.elements.namedItem('id');
    const quantity = this.refs.form.elements.namedItem('quantity');
    if (!(input instanceof HTMLInputElement) || !(quantity instanceof HTMLInputElement)) return;
    event.preventDefault();
    for (const pack of this.querySelectorAll('a[data-variant]')) {
      if (pack.dataset.variant === link.dataset.variant) pack.setAttribute('aria-current', 'true');
      else pack.removeAttribute('aria-current');
    }
    input.value = link.dataset.variant;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    quantity.min = link.dataset.min;
    quantity.step = link.dataset.step;
    if (link.dataset.max) quantity.max = link.dataset.max;
    else quantity.removeAttribute('max');
    if (!quantity.checkValidity()) quantity.value = quantity.min;
    const available = link.dataset.available === 'true';
    const stockLabel = this.#updateStock(input.value);
    this.refs.add.disabled = !available;
    this.refs.add.dataset.variantAvailable = String(available);
    for (const node of this.querySelectorAll('[data-selected-price]')) node.textContent = link.dataset.price;
    for (const node of this.querySelectorAll('[data-selected-pack-name]')) node.textContent = link.dataset.packName || '';
    for (const node of this.querySelectorAll('[data-add-label]')) node.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
    for (const node of this.querySelectorAll('[data-add-price]')) node.hidden = !available;
    for (const node of this.querySelectorAll('[data-sticky-submit]')) node.disabled = !available;
    this.#syncPurchase();
    const checkout = this.refs.form.querySelector('.ev-product__checkout');
    if (checkout instanceof HTMLElement) checkout.hidden = !available;
    const url = new URL(location.href);
    url.searchParams.set('variant', input.value);
    history.replaceState(null, '', url);
    const status = this.querySelector('[data-pack-status]');
    if (status) status.textContent = [link.dataset.selection, stockLabel].filter(Boolean).join('. ');
  }

  /** @param {Event} event - Native purchase radio change; CSS also updates prices before JavaScript loads. */
  changePurchaseType(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#syncPurchase();
    const status = this.querySelector('[data-pack-status]');
    const selected = this.querySelector('[data-subscription-radio]:checked');
    const terms = this.querySelector('[data-subscription-terms-variant]:not([hidden])');
    if (status) status.textContent = selected ? terms?.textContent || '' : event.target.closest('label')?.textContent || '';
  }

  /** @param {Event} event - Lower card checkbox selects its pack and shared purchase type. */
  changeLowerPurchase(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    const checked = event.target.checked;
    const id = event.target.dataset.lowerSubscription;
    const pack = [...this.querySelectorAll('.ev-packs a[data-variant]')].find((node) => node.dataset.variant === id);
    if (!(pack instanceof HTMLAnchorElement)) return;
    pack.click();
    const subscription = this.querySelector('[data-subscription-radio]');
    if (!(subscription instanceof HTMLInputElement) || subscription.disabled) return;
    subscription.checked = checked;
    const oneTime = this.querySelector('[data-one-time-radio]');
    if (oneTime instanceof HTMLInputElement) oneTime.checked = !checked;
    this.changePurchaseType({ target: subscription });
  }

  /** Keep both purchase paths on the selected pack; unsupported packs always return to one-time. */
  #syncPurchase() {
    const subscription = this.querySelector('[data-subscription-radio]');
    const pack = this.querySelector('a[data-variant][aria-current="true"]');
    for (const node of this.querySelectorAll('[data-buy-price]')) node.hidden = node.dataset.buyPrice !== pack?.dataset.variant;
    if (!(subscription instanceof HTMLInputElement)) return;
    const row = subscription.closest('label');
    const plan = pack?.dataset.sellingPlan;
    subscription.value = plan || '';
    subscription.disabled = !plan;
    if (row) row.hidden = !plan;
    const options = subscription.closest('fieldset');
    if (options) options.hidden = !plan;
    const label = this.querySelector('[data-subscription-row] [data-refill-label]');
    if (label) label.textContent = pack?.dataset.refillLabel || '';
    for (const node of this.querySelectorAll('[data-purchase-renewal]')) node.textContent = pack?.dataset.renewal || '';
    for (const node of this.querySelectorAll('[data-purchase-subscription-price]')) node.textContent = pack?.dataset.subscriptionPrice || '';
    for (const node of this.querySelectorAll('[data-one-time-price]')) node.textContent = pack?.dataset.price || '';
    if (!plan) subscription.checked = false;
    const oneTime = this.querySelector('[data-one-time-radio]');
    if (oneTime instanceof HTMLInputElement) oneTime.checked = !subscription.checked;
    for (const lower of this.querySelectorAll('[data-lower-subscription]')) lower.checked = subscription.checked;
    const price = pack?.querySelector('.ev-subscription-price');
    for (const node of this.querySelectorAll('[data-subscription-selected]')) node.innerHTML = price?.innerHTML || '';
    for (const node of this.querySelectorAll('[data-subscription-terms-variant]')) node.hidden = node.dataset.subscriptionTermsVariant !== pack?.dataset.variant;
  }

  /** Select a lower offer through the shared purchase state before native submission.
   * @param {MouseEvent} event - A lower pricing card's add button.
   */
  addPack(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-add-pack]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const link = [...this.querySelectorAll('.ev-packs a[data-variant]')].find((pack) => pack.dataset.variant === button.dataset.addPack);
    if (!(link instanceof HTMLAnchorElement)) return;
    link.click();
    this.submitSticky();
  }

  /** Return focus to the actual form when quantity validation blocks a sticky submission. */
  submitSticky() {
    if (!this.refs.form.checkValidity()) {
      this.refs.form.scrollIntoView({ block: 'center', behavior: 'instant' });
      this.refs.form.reportValidity();
      return;
    }
    this.refs.form.requestSubmit(this.refs.add);
  }
}

/** Native header disclosures: Escape, outside pointer and link selection restore sensible state.
 * @extends {Component<{panels: HTMLDetailsElement[]}>}
 */
class EvenmornNavigation extends Component {
  #events;
  #scrollY = 0;
  #frame;

  connectedCallback() { super.connectedCallback(); this.#initialize(); }
  updatedCallback() { super.updatedCallback(); this.#initialize(); }
  disconnectedCallback() {
    this.#events?.abort();
    cancelAnimationFrame(this.#frame);
    super.disconnectedCallback();
  }

  #initialize() {
    this.#events?.abort();
    cancelAnimationFrame(this.#frame);
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.#scrollY = scrollY;
    this.addEventListener('keydown', this.#keyboard, { signal });
    this.addEventListener('click', this.#link, { signal });
    window.addEventListener('pointerdown', this.#outside, { signal });
    window.addEventListener('scroll', this.#scroll, { passive: true, signal });
  }

  #close() { for (const panel of this.refs.panels || []) panel.open = false; }
  /** @param {KeyboardEvent} event - Escape from an open navigation/search disclosure. */
  #keyboard = (event) => {
    if (event.key !== 'Escape') return;
    const panel = this.refs.panels?.find(item => item.open && event.target instanceof Node && item.contains(event.target));
    if (!panel) return;
    event.preventDefault();
    panel.open = false;
    panel.querySelector('summary')?.focus();
  };
  /** @param {MouseEvent} event - Keep native link navigation, then close disclosures. */
  #link = (event) => { if (event.target instanceof Element && event.target.closest('a')) this.#close(); };
  /** @param {PointerEvent} event - Pointer dismissal outside the header. */
  #outside = (event) => { if (event.target instanceof Node && !this.contains(event.target)) this.#close(); };
  #scroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (Math.abs(scrollY - this.#scrollY) > 4) {
        this.toggleAttribute('data-scrolling-down', scrollY > this.#scrollY && scrollY > 150 && !this.refs.panels?.some(panel => panel.open));
        this.#scrollY = scrollY;
      }
    });
  };
}

for (const [name, element] of [['evenmorn-carousel', EvenmornCarousel], ['evenmorn-product', EvenmornProduct], ['evenmorn-navigation', EvenmornNavigation]]) {
  if (!customElements.get(name)) customElements.define(name, element);
}
